import { Space } from "antd";
import { FC, useEffect, useState } from "react";
import { useLoading } from "../../context/LoadingProvider/useLoading";
import { ActionButtonEnum } from "../../enums/actionButtonEnum";
import { SearchOptionEnum } from "../../enums/searchOptionEnum";
import useActionButtons from "../../hooks/useActionButtons";
import useSmartSearchParams, {
  SmartSearchParams,
} from "../../hooks/useSmartSearchParams";
import { Output } from "../../services/types";
import { isString } from "../../utils/stringUtils";
import Button from "../UI/Button";
import SmartSearch from "../UI/SmartSearch";
import DefaultFormParserLayout from "./default-form-parser-layout";
import DateInput from "../UI/DateInput";
import dayjs from "dayjs";

export type SmartSearchDateActionsProps = {
  searchBy: SearchOptionEnum[];
  actions?: ActionButtonEnum[];
  validOnEmpty?: boolean;
  validateSearch?: boolean;
  dateInputLabel?: string;
  onRefresh: (
    output: Output,
    params: SmartSearchParams,
    date: dayjs.Dayjs
  ) => Promise<string | void>;
};

const SmartSearchDateActions: FC<SmartSearchDateActionsProps> = ({
  searchBy,
  actions = [],
  validOnEmpty = true,
  validateSearch = true,
  dateInputLabel,
  onRefresh,
}) => {
  const TODAY = dayjs();

  const loading = useLoading();

  const [search, setSearch] = useState("");
  const [report, setReport] = useState("");
  const [params, setParams] = useSmartSearchParams();
  const [validSearch, setValidSearch] = useState(false);
  const [timeInterval, setTimeInterval] = useState(TODAY);

  const disabled = !validSearch || (validateSearch && !search.length);

  const {
    isToShowRefreshAction,
    isToShowSaveCSVAction,
    isToShowSavePDFAction,
  } = useActionButtons(actions);

  const handleEnter = (event: KeyboardEvent) => {
    if (loading.loading) return;
    if (!event.repeat && event.key === 'Enter') {
      refreshReport()
    }
  }

  useEffect(() => {
    document.addEventListener("keydown", handleEnter)
    return () => document.removeEventListener("keydown", handleEnter)
  })

  const refreshReport = async (output: Output = Output.HTML) => {
    loading.addLoading();

    const report = await onRefresh(output, params, timeInterval);

    if (isString(report) && Output.isHTML(output)) {
      setReport(report);
    }

    loading.removeLoading();
  };

  return (
    <DefaultFormParserLayout html={report}>
      <Space direction="horizontal" size="middle" wrap>
        <SmartSearch
          autoFocus
          validOnEmpty={validOnEmpty}
          label="Smart Search"
          status="active,closed"
          searchBy={searchBy}
          onChange={(e: string) => setSearch(e)}
          value={search}
          onValidSearch={setValidSearch}
          onObjValueChange={(e: any) => setParams({ ...params, ...e })}
        />

        <DateInput
          label={dateInputLabel}
          defaultValue={TODAY}
          onChange={(e) => setTimeInterval(e)}
          format="MM-DD-YYYY"
          allowClear={false}
        />

        {isToShowRefreshAction && (
          <Button
            type="primary"
            disabled={disabled}
            onClick={() => refreshReport()}
          >
            Refresh
          </Button>
        )}

        {isToShowSaveCSVAction && (
          <Button
            type="primary"
            disabled={disabled}
            onClick={() => refreshReport(Output.CSV)}
          >
            Save as CSV
          </Button>
        )}

        {isToShowSavePDFAction && (
          <Button
            type="primary"
            disabled={disabled}
            onClick={() => refreshReport(Output.PDF)}
          >
            Save as PDF
          </Button>
        )}
      </Space>
    </DefaultFormParserLayout>
  );
};

export default SmartSearchDateActions;
